"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePicker = void 0;
var jsx_runtime_1 = require("react/jsx-runtime");
var classnames_1 = require("classnames");
var dayjs = require("dayjs");
var localizedFormat = require("dayjs/plugin/localizedFormat");
var react_1 = require("react");
var theme_1 = require("../../theme");
var buttons_1 = require("../buttons");
var TextInput_1 = require("../TextInput");
var Typography_1 = require("../Typography");
var DatePickerContent_1 = require("./DatePickerContent");
var hooks_1 = require("./hooks");
dayjs.extend(localizedFormat);
var types_1 = require("./types");
var DatePicker = function (_a) {
    var dataTestId = _a["data-testid"], placeholder = _a.placeholder, disabled = _a.disabled, editable = _a.editable, icon = _a.icon, useActionButtons = _a.useActionButtons, closeOnSelect = _a.closeOnSelect, _b = _a.cancelButtonText, cancelButtonText = _b === void 0 ? 'Cancel' : _b, _c = _a.selectButtonText, selectButtonText = _c === void 0 ? 'Choose date' : _c, _d = _a.currentDayText, currentDayText = _d === void 0 ? 'Current day' : _d, _e = _a.selectedDayText, selectedDayText = _e === void 0 ? 'Selected day' : _e, _f = _a.weekDays, weekDays = _f === void 0 ? types_1.defaultWeekDaysText : _f, _g = _a.months, months = _g === void 0 ? types_1.defaultMonthText : _g, value = _a.value, disableBefore = _a.disableBefore, disableAfter = _a.disableAfter, disableYearsBefore = _a.disableYearsBefore, disableYearsAfter = _a.disableYearsAfter, onChange = _a.onChange, parentRef = _a.parentRef, _h = _a.position, position = _h === void 0 ? { bottom: true, left: true } : _h, _j = _a.format, format = _j === void 0 ? 'YYYY-MM-DD' : _j, mask = _a.mask, containerClassName = _a.containerClassName, _k = _a.locale, locale = _k === void 0 ? 'en' : _k, id = _a.id, label = _a.label, labelHidden = _a.labelHidden;
    var borderColors = (0, theme_1.useBorderColors)();
    var backgroundColors = (0, theme_1.useBackgroundColors)();
    var _l = (0, react_1.useState)(false), open = _l[0], setOpen = _l[1];
    var _m = (0, react_1.useState)(value), selectedDate = _m[0], setSelectedDate = _m[1];
    var inputRef = (0, react_1.useRef)(null);
    var dropDownRef = (0, react_1.useRef)(null);
    var cancelButtonRef = (0, react_1.useRef)(null);
    var selectButtonRef = (0, react_1.useRef)(null);
    var contentRef = (0, react_1.useRef)(null);
    var _o = (0, react_1.useState)(value), dateValue = _o[0], setDateValue = _o[1];
    var _p = (0, react_1.useState)(value
        ? dayjs(value)
            .locale(locale || 'en')
            .format(format)
        : ''), textInput = _p[0], setTextInput = _p[1];
    (0, react_1.useEffect)(function () {
        var handleClickOutside = function (event) {
            var _a, _b, _c, _d;
            if (!((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target))) {
                if ((useActionButtons &&
                    (((_b = cancelButtonRef === null || cancelButtonRef === void 0 ? void 0 : cancelButtonRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.target)) ||
                        ((_c = selectButtonRef === null || selectButtonRef === void 0 ? void 0 : selectButtonRef.current) === null || _c === void 0 ? void 0 : _c.contains(event.target)))) ||
                    (!useActionButtons &&
                        closeOnSelect &&
                        !((_d = contentRef.current) === null || _d === void 0 ? void 0 : _d.isClicked(event.target)))) {
                    setOpen(false);
                }
            }
        };
        window.addEventListener('click', handleClickOutside);
        return function () {
            window.removeEventListener('click', handleClickOutside);
        };
    }, [
        inputRef,
        cancelButtonRef,
        selectButtonRef,
        contentRef,
        useActionButtons,
    ]);
    (0, react_1.useEffect)(function () {
        setSelectedDate(value);
    }, [value]);
    (0, react_1.useEffect)(function () {
        setDateValue(selectedDate);
        setTextInput(selectedDate
            ? dayjs(selectedDate)
                .locale(locale || 'en')
                .format(format)
            : '');
    }, [selectedDate]);
    var style = (0, hooks_1.useDatePickerPositioning)(inputRef, dropDownRef, open, parentRef, position);
    var handleTextInputChange = function (input) {
        setTextInput(input);
        if (input.length === ((mask === null || mask === void 0 ? void 0 : mask.length) || input.length)) {
            var rawDate = dayjs(dayjs(input.trim()).format(format));
            var date = rawDate.toDate();
            if (rawDate.isValid()) {
                var disabled_1 = (!!disableBefore && rawDate.isBefore(disableBefore, 'day')) ||
                    (!!disableAfter && rawDate.isAfter(disableAfter, 'day'));
                if (!disabled_1) {
                    setSelectedDate(date);
                    setDateValue(date);
                    if (!useActionButtons) {
                        onChange === null || onChange === void 0 ? void 0 : onChange(date, rawDate.format(format));
                    }
                }
            }
        }
    };
    var handleContentSelectDate = function (date) {
        setSelectedDate(date);
        setDateValue(date);
        var formatted = date
            ? dayjs(date)
                .locale(locale || 'en')
                .format(format)
            : '';
        setTextInput(formatted);
        if (!useActionButtons) {
            onChange === null || onChange === void 0 ? void 0 : onChange(date, formatted);
            if (closeOnSelect) {
                setOpen(false);
            }
        }
    };
    var handleCancel = function () {
        setSelectedDate(undefined);
        setDateValue(value);
        setTextInput('');
    };
    var handleChange = function () {
        onChange === null || onChange === void 0 ? void 0 : onChange(dayjs(dayjs(selectedDate).format(format)).toDate(), dayjs(selectedDate).format(format));
    };
    var handleFocus = function () {
        setOpen(true);
    };
    var handleToggle = function () {
        setOpen(true);
    };
    return ((0, jsx_runtime_1.jsxs)("div", __assign({ className: (0, classnames_1.default)('squid-relative squid-inline-block', containerClassName) }, { children: [label && ((0, jsx_runtime_1.jsx)(Typography_1.Label, __assign({ as: "label", htmlFor: id, color: "weakest", className: "mb-2 ".concat(labelHidden ? 'squid-sr-only' : '') }, { children: label }))), (0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)("div", __assign({ "data-testid": "".concat(dataTestId, "Button"), onClick: !disabled ? handleToggle : undefined, ref: inputRef, className: "squid-w-full" }, { children: (0, jsx_runtime_1.jsx)(TextInput_1.TextInput, { id: id, placeholder: placeholder, disabled: disabled, editable: editable, icon: icon, value: textInput, onFocus: !disabled ? handleFocus : undefined, onChange: !disabled && editable ? handleTextInputChange : undefined, "data-testid": dataTestId, mask: mask, combo: true }) })) }), open && ((0, jsx_runtime_1.jsxs)("div", __assign({ className: (0, classnames_1.default)("\n          squid-absolute\n          squid-my-2\n          squid-z-50 \n          squid-flex \n          squid-flex-col \n          squid-w-80 \n          squid-px-[20px] \n          squid-py-[16px] \n          squid-border \n          squid-rounded-core-border-radius-md\n          ", borderColors.base, backgroundColors.base), style: style, ref: dropDownRef, "data-testid": "".concat(dataTestId, "Menu") }, { children: [(0, jsx_runtime_1.jsx)(DatePickerContent_1.default, { forwardedRef: contentRef, onChange: handleContentSelectDate, value: dateValue, selectedValue: selectedDate, disableBefore: disableBefore, disableAfter: disableAfter, disableYearsBefore: disableYearsBefore, disableYearsAfter: disableYearsAfter, months: months.length !== 12 ? types_1.defaultMonthText : months, weekDays: weekDays.length !== 7 ? types_1.defaultWeekDaysText : weekDays, "data-testid": dataTestId }), useActionButtons && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-flex squid-items-center squid-justify-center squid-gap-[20px] squid-my-4" }, { children: [(0, jsx_runtime_1.jsxs)("span", __assign({ className: "squid-flex squid-items-center squid-gap-1" }, { children: [(0, jsx_runtime_1.jsx)("div", { className: "squid-w-2 squid-h-2 squid-border squid-border-alias-color-border-active squid-rounded-full" }), (0, jsx_runtime_1.jsx)(Typography_1.Label, __assign({ strong: false, className: "squid-text-alias-color-text-weaker squid-text-center" }, { children: currentDayText }))] })), (0, jsx_runtime_1.jsxs)("span", __assign({ className: "squid-flex squid-items-center squid-gap-[4px]" }, { children: [(0, jsx_runtime_1.jsx)("div", { className: "squid-w-2 squid-h-2 squid-bg-alias-color-bg-primary squid-rounded-full" }), (0, jsx_runtime_1.jsx)(Typography_1.Label, __assign({ strong: false, className: "squid-text-alias-color-text-weaker squid-text-center" }, { children: selectedDayText }))] }))] })), (0, jsx_runtime_1.jsxs)("div", __assign({ className: "squid-flex squid-p-4 squid-gap-4 squid-border-t squid-items-center squid-justify-evenly squid-border-alias-color-border-base" }, { children: [(0, jsx_runtime_1.jsx)(buttons_1.Button, __assign({ variant: "critical", onClick: handleCancel, size: "sm", forwardedRef: cancelButtonRef, tabIndex: 0 }, { children: cancelButtonText })), (0, jsx_runtime_1.jsx)(buttons_1.Button, __assign({ variant: "primary", onClick: handleChange, size: "sm", forwardedRef: selectButtonRef, tabIndex: 0 }, { children: selectButtonText }))] }))] }))] })))] })));
};
exports.DatePicker = DatePicker;
